import { formatName } from "@/lib/utils/preferences";
import type { AssignmentListData } from "@/react/components/tearsheets/assignment/types";
import { getAttachedDate } from "@laborchart-modules/common/dist/datetime";
import {
   Button,
   Flex,
   Modal,
   SegmentedController,
   Table,
   Typography,
   useI18nContext,
} from "@procore/core-react";

import React, { useEffect, useState } from "react";
import { formatDateTime } from "../helper";

enum AssignmentState {
   VALID = "valid",
   INVALID = "invalid",
}

type ConflictModalProps = {
   isOpen: boolean;
   onClose: () => void;
   onSave: () => void;
   validAssignments: AssignmentListData[];
   invalidAssignments: Array<{ assignment: AssignmentListData; reason: string }>;
   selectedCount: number;
};

export const AssignmentConflictModal: React.FC<ConflictModalProps> = ({
   isOpen,
   onClose,
   onSave,
   validAssignments,
   invalidAssignments,
   selectedCount,
}) => {
   // by default conflicts are shown
   const [selectedView, setSelectedView] = useState<AssignmentState>(AssignmentState.INVALID);
   const I18n = useI18nContext();

   useEffect(() => {
      setSelectedView(AssignmentState.INVALID);
   }, [isOpen]);

   return (
      <Modal
         open={isOpen}
         onClose={onClose}
         width="lg"
         style={{ maxHeight: "600px", maxWidth: "900px" }}
      >
         <Modal.Header style={{ padding: "10px 32px" }}>
            {I18n.t("views.company.workforce_planning.assignment.modal.conflicts")}
         </Modal.Header>
         <Modal.Body style={{ padding: "0px 32px" }}>
            <Flex direction="column" alignItems="center">
               <Typography>
                  {validAssignments.length > 0
                     ? I18n.t("views.company.workforce_planning.assignment.modal.modal_help_text", {
                          valid_records: validAssignments.length,
                          total_records: selectedCount,
                       })
                     : I18n.t(
                          "views.company.workforce_planning.assignment.bulk.edit.validation.batch_edit",
                       )}
               </Typography>

               {validAssignments.length > 0 && (
                  <SegmentedController
                     aria-label="assignment-segmented-controller"
                     style={{ marginTop: "20px", width: "50%" }}
                  >
                     <SegmentedController.Segment
                        onClick={() => setSelectedView(AssignmentState.INVALID)}
                        selected={selectedView === AssignmentState.INVALID}
                     >
                        {I18n.t("views.company.workforce_planning.assignment.modal.conflicts")} (
                        {invalidAssignments.length})
                     </SegmentedController.Segment>
                     <SegmentedController.Segment
                        onClick={() => setSelectedView(AssignmentState.VALID)}
                        selected={selectedView === AssignmentState.VALID}
                     >
                        {I18n.t("views.company.workforce_planning.assignment.modal.valid")} (
                        {validAssignments.length})
                     </SegmentedController.Segment>
                  </SegmentedController>
               )}
            </Flex>

            <Table.Container
               style={{
                  marginTop: "20px",
                  maxHeight: "400px",
                  overflowY: "auto",
                  overflowX: "auto",
               }}
            >
               <Table>
                  <Table.Header
                     style={{ whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 1 }}
                  >
                     <Table.HeaderRow>
                        <Table.HeaderCell>
                           {I18n.t("views.company.workforce_planning.project")}
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                           {I18n.t("views.company.workforce_planning.labor")}
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                           {I18n.t("views.company.workforce_planning.start_date")}
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                           {I18n.t("views.company.workforce_planning.end_date")}
                        </Table.HeaderCell>
                        {selectedView === AssignmentState.INVALID && (
                           <Table.HeaderCell>
                              {I18n.t("views.company.workforce_planning.assignment.modal.conflict")}
                           </Table.HeaderCell>
                        )}
                     </Table.HeaderRow>
                  </Table.Header>
                  <Table.Body>
                     {selectedView === AssignmentState.INVALID &&
                        invalidAssignments.map(({ assignment, reason }, index) => (
                           <Table.BodyRow key={`conflict-${assignment.id}-${index}`}>
                              <Table.BodyCell>
                                 <Table.TextCell>{assignment.project}</Table.TextCell>
                              </Table.BodyCell>
                              <Table.BodyCell>
                                 <Table.TextCell>
                                    {assignment.person?.name
                                       ? formatName(assignment.person.name)
                                       : null}
                                 </Table.TextCell>
                              </Table.BodyCell>
                              <Table.BodyCell>
                                 <Table.TextCell>
                                    {assignment.start_day
                                       ? formatDateTime(getAttachedDate(assignment.start_day), {
                                            locale: I18n.locale,
                                            isRequiredTimezone: false,
                                         })
                                       : null}
                                 </Table.TextCell>
                              </Table.BodyCell>
                              <Table.BodyCell>
                                 <Table.TextCell>
                                    {assignment.end_day
                                       ? formatDateTime(getAttachedDate(assignment.end_day), {
                                            locale: I18n.locale,
                                            isRequiredTimezone: false,
                                         })
                                       : null}
                                 </Table.TextCell>
                              </Table.BodyCell>
                              <Table.BodyCell>
                                 <Table.TextCell style={{ color: "#d4061f" }}>
                                    {reason}
                                 </Table.TextCell>
                              </Table.BodyCell>
                           </Table.BodyRow>
                        ))}
                     {selectedView === AssignmentState.VALID &&
                        validAssignments.map((assignment, index) => (
                           <Table.BodyRow key={`valid-${assignment.id}-${index}`}>
                              <Table.BodyCell>
                                 <Table.TextCell>{assignment.project}</Table.TextCell>
                              </Table.BodyCell>
                              <Table.BodyCell>
                                 <Table.TextCell>
                                    {assignment.person?.name
                                       ? formatName(assignment.person.name)
                                       : null}
                                 </Table.TextCell>
                              </Table.BodyCell>
                              <Table.BodyCell>
                                 <Table.TextCell>
                                    {assignment.start_day
                                       ? formatDateTime(getAttachedDate(assignment.start_day), {
                                            locale: I18n.locale,
                                            isRequiredTimezone: false,
                                         })
                                       : null}
                                 </Table.TextCell>
                              </Table.BodyCell>
                              <Table.BodyCell>
                                 <Table.TextCell>
                                    {assignment.end_day
                                       ? formatDateTime(getAttachedDate(assignment.end_day), {
                                            locale: I18n.locale,
                                            isRequiredTimezone: false,
                                         })
                                       : null}
                                 </Table.TextCell>
                              </Table.BodyCell>
                           </Table.BodyRow>
                        ))}
                  </Table.Body>
               </Table>
            </Table.Container>
         </Modal.Body>
         <Modal.Footer>
            <Modal.FooterButtons>
               <Button variant="secondary" onClick={onClose}>
                  {I18n.t("views.company.workforce_planning.cancel")}
               </Button>
               {validAssignments.length > 0 && (
                  <Button variant="primary" onClick={onSave}>
                     {I18n.t("views.company.workforce_planning.save")}
                  </Button>
               )}
            </Modal.FooterButtons>
         </Modal.Footer>
      </Modal>
   );
};
